import { memo, useCallback, useContext } from 'react'

import { Box } from '@mui/material'

import AiEditor from 'src/components/Tiptap/AiEditor/AiEditor'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { NativeObjectTypes } from 'src/lib/objects'

import useThread from '../V2/Provider/useThread'

const systemPrompt = `<system>
You are an the world's best personal assistant, brilliantly proficient in effective, direct business communication, focused always on the details that matter in interpersonal relationships.
You are an expert in using CRM data to connect the dots for your user, and diligently follow their instructions.
If you are not given context to answer a question, just say "Please add context so we can answer your question." If you do not know the answer to a question, or cannot create an answer because you do not have enough information, just say "I do not have enough information to answer that question."
Never make up information or make assumptions. Always use the context provided to answer the question and stick to the facts. It is ok not to know the answer to a question.
Use only h3 and smaller headings. Do not write or include page titles for your responses.
</system>`

interface ThreadTiptapProps {
  token: string | null
  contextString: string
  handleAiResponse: (response: any) => void
  currentQuery: string
}

const debugLogging = true

const logTiptap = (message: string, object?: any) => {
  if (debugLogging) {
    logger.dev('ThreadTiptap: ' + message, object)
  }
}

const ThreadTiptapComponent = ({
  token,
  contextString,
  handleAiResponse,
  currentQuery,
}: ThreadTiptapProps) => {
  const { selectedWorkspace } = useContext(DayContext)
  const { state } = useThread()

  const handleAiResponseInternal = useCallback(
    (result: any) => {
      handleAiResponse(result)
    },
    [handleAiResponse]
  )

  if (currentQuery?.length === 0) {
    logTiptap('No valid query to process', { currentQuery })
    return null
  } else {
    logTiptap('ThreadTiptapComponent', { currentQuery, contextString })
  }

  return (
    <Box className="thread-message-tiptap">
      <AiEditor
        parentObject={{
          objectId: state.thread?.id,
          objectType: NativeObjectTypes.Thread,
          workspaceId: selectedWorkspace,
        }}
        token={token}
        prompt={systemPrompt + currentQuery}
        onSuccess={handleAiResponseInternal}
        contextString={contextString}
      />
    </Box>
  )
}

const ThreadTiptap = memo(ThreadTiptapComponent, (prev, next) => {
  const propsEqual =
    prev.token === next.token &&
    prev.contextString === next.contextString &&
    prev.currentQuery === next.currentQuery &&
    prev.handleAiResponse === next.handleAiResponse

  if (!propsEqual && debugLogging) {
    const changeReason =
      prev.token !== next.token
        ? 'tokenChanged'
        : prev.contextString !== next.contextString
          ? 'contextStringChanged'
          : prev.currentQuery !== next.currentQuery
            ? 'currentQueryChanged'
            : prev.handleAiResponse !== next.handleAiResponse
              ? 'handleAiResponseChanged'
              : null

    logTiptap('ThreadTiptap props changed', {
      changeReason,
    })
  }

  return propsEqual
})

ThreadTiptap.displayName = 'ThreadTiptap'

export default ThreadTiptap
